import { action, computed, observable, makeObservable } from 'mobx'
import { CONFIG } from '../../config'
import { IShopifyCheckout, IShopifyLineItem } from '../../interfaces'
import { shopifyCartService } from '../../services'
import { animationStore, customerStore } from '../../stores'
import { analyticsService } from '../../services'
import { retrieveFromWindow } from '../../utils'
import { collectionsStore, loggedIn } from '../collections'
import { IProduct } from '../product'
import { ICartItem, ICartUpsell, IShopifyCart } from './cart.interfaces'
import { collectionListStore } from '../collections/collection-list/collection-list.store'
import { format, parse } from 'date-fns'

const cartUpsell: ICartUpsell = retrieveFromWindow('cartUpsell', {})
const productData: IProduct[] = retrieveFromWindow('productData', [])
const shopifyCart: IShopifyCart = retrieveFromWindow('cart', [])

class CartStore {
  readonly pulseAnimationKey: string = 'cartPulse'
  @observable cartUpsell: ICartUpsell = cartUpsell
  @observable checkout: IShopifyCheckout | null = null
  @observable hasHydrated: boolean = false
  @observable isSubmitting: boolean = false
  @observable itemQuantityInputs: Map<string, string> = new Map()

  @observable items: Map<string, ICartItem> = new Map()
  @observable showCart: boolean = false
  @observable slideInCart: boolean = false
  @observable isSetting: boolean = false
  @observable deliveryDate: string = ''
  @observable deliveryTime: string = ''
  @observable deliveryWeek: string = ''
  @observable shippingMethod: string = ''
  @observable removeItemFromCartLoading: string = ''

  constructor() {
    this.initialize()

    makeObservable(this)

    const selectedDeliveryDay = JSON.parse(sessionStorage.getItem('selectedDeliveryDate'))

    if (selectedDeliveryDay) {
      this.deliveryDate = selectedDeliveryDay.delivery_date

      const deliveryTime = this.formatDeliveryTime(
        selectedDeliveryDay.delivery_date,
        selectedDeliveryDay.delivery_times[0].start_time,
        selectedDeliveryDay.delivery_times[0].end_time
      )

      this.setDeliveryTime(deliveryTime)
    }
  }

  async initialize(): Promise<void> {
    animationStore.register(this.pulseAnimationKey, CONFIG.animation.timeouts.cartPulse)
    customerStore.id && (await this.hydrateCart())
    this.hasHydrated = true
    if (this.items.size > 0) {
      animationStore.start(this.pulseAnimationKey)
    }
  }

  @action
  createAjaxCart = async (): Promise<void> => {
    this.isSubmitting = true

    const response = await shopifyCartService.createCart(this.formatCartInput)

    if (response) {
      location.href = `//${location.hostname}/checkout`
    } else {
      // @TODO This "shouldn't" ever happen, so log to Sentry
      this.isSubmitting = false
    }
  }

  @action
  hydrateCart = async (): Promise<void> => {
    if (shopifyCart && shopifyCart.items && shopifyCart.items.length > 0) {
      shopifyCart.items.forEach(({ product_id, quantity }) => {
        const product = productData[product_id]

        if (product) {
          this.items.set(product.id, { product, quantity })
          this.itemQuantityInputs.set(product.id, quantity.toString())
        }
      })
    }
  }

  @action
  toggleShowCart = (): void => {
    const { backdrop, cartSlideIn } = CONFIG.animation.timeouts
    if (!this.showCart && this.minimumDollarAmountHasBeenMet) {
      this.showCart = true
      window.setTimeout(() => (this.slideInCart = true), backdrop)
    } else if (this.showCart) {
      this.slideInCart = false
      window.setTimeout(() => (this.showCart = false), cartSlideIn)
    }
  }

  @action
  updateOrAddItemToCart = (product: IProduct, quantity: number, sendToShopify: boolean = true): void => {
    const item = this.items.get(product.id)
    const existingItemUpdated: boolean = item && quantity !== item.quantity
    const newItemAdded: boolean = !item && quantity > 0

    if (!loggedIn) {
      // redirect to login
      window.location.href = '/account/login'
      return
    }

    if (existingItemUpdated) {
      if (quantity === 0) {
        analyticsService.sendEvent({ eventName: 'productRemoveFromCart', fields: { product: product }, screen: 'Orders' })

        this.items.delete(product.id)

        if (sendToShopify) {
          shopifyCartService.updateQuantity(product.variantId, 0)
        }
      } else {
        analyticsService.sendEvent({ eventName: 'productUpdateInCart', fields: { product, quantity }, screen: 'Orders' })

        this.items.set(product.id, { ...item, quantity })

        if (sendToShopify) {
          shopifyCartService.updateQuantity(product.variantId, quantity)
        }
      }
    } else if (newItemAdded) {
      analyticsService.sendEvent({ eventName: 'productAddToCart', fields: { product, quantity }, screen: 'Orders' })

      this.items.set(product.id, { quantity, product })

      if (sendToShopify) {
        shopifyCartService.addToCart(product.variantId, quantity)
      }
    }

    if (existingItemUpdated || newItemAdded) {
      animationStore.start(this.pulseAnimationKey)
    }
  }

  @action
  removeFromCart = (product: IProduct): void => {
    // prevent action when loading
    if (this.removeItemFromCartLoading !== '') {
      return
    }
    analyticsService.sendEvent({ eventName: 'productRemoveFromCart', fields: { product: product }, screen: 'Orders' })

    this.removeItemFromCartLoading = product.id
    shopifyCartService
      .updateQuantity(product.variantId, 0)
      .then((result) => {
        if (result) {
          this.items.delete(product.id)
        }
      })
      .finally(() => {
        this.removeItemFromCartLoading = ''
      })
  }

  @action
  clearCart = (): void => {
    const payload: Record<string, number> = {}
    this.items.forEach(({ product }) => {
      payload[product.variantId] = 0
    })
    shopifyCartService.updateQuantityItems(payload).then((result) => {
      if (result) {
        this.items.clear()
      }
    })

    this.items.clear()
    this.itemQuantityInputs.clear()
    this.checkout = null
  }

  getInputQuantityForProduct(productId: string): number {
    return parseInt(this.itemQuantityInputs.get(productId), 10) || 0
  }

  isProductInCurrentRotationMenu(productId: string): boolean {
    var currentRotationalMenu = collectionsStore.getRotationalMenu()

    if (!currentRotationalMenu) {
      return true
    }

    const collections = collectionsStore.links

    return collections.some((collection) => {
      const products = collectionListStore.displayCollections(collection.handle)

      return products.some((product) => product.id === productId)
    })
  }

  @action
  formatDeliveryTime = (deliveryDay: string, timeStart: string, timeEnd: string): string => {
    const parsedStart = parse(timeStart, 'HH:mm:ss', new Date())
    const formattedTimeStart = format(parsedStart, 'h:mma')
    const parsedEnd = parse(timeEnd, 'HH:mm:ss', new Date())
    const formattedTimeEnd = format(parsedEnd, 'h:mma')

    return `${formattedTimeStart} to ${formattedTimeEnd}`
  }

  @action
  setDeliveryTime = (deliveryTime: string): void => {
    this.deliveryTime = deliveryTime

    sessionStorage.setItem('selectedDeliveryTime', JSON.stringify(deliveryTime))
  }

  isProductNotAvailable(productId: string): boolean {
    const products = collectionListStore.getCurrentProducts()
    return !products.some((product) => product.id === productId)
  }

  @computed
  get hasUnavailableItems(): boolean {
    return this.itemsNormalized.some(({ product }) => this.isProductNotAvailable(product.id))

    // const collections = collectionsStore.links
    // const normalCollections = collections.filter((collection) => !collection.deliveryWeek)
    // const normalProducts = []
    // for (const collection of normalCollections) {
    //   normalProducts.push(...collectionListStore.displayCollections(collection.handle))
    // }

    // const productIds = normalProducts.map((product) => product.id)
    // const itemsInSpecialMenu = this.itemsNormalized.filter(({ product }) => !productIds.includes(product.id))
    // return itemsInSpecialMenu.some(({ product }) => !this.isProductInCurrentRotationMenu(product.id))
  }

  @computed
  get cartHasItems(): boolean {
    return this.items.size > 0
  }

  @computed
  get dollarAmount(): number {
    return this.itemsNormalized.reduce((prev, { product, quantity }) => prev + product.price * quantity, 0)
  }

  @computed
  get formatCartInput(): IShopifyLineItem[] {
    const items: IShopifyLineItem[] = []

    cartStore.itemsNormalized.map(({ product: { variantId }, quantity }) => {
      items.push({
        id: variantId,
        quantity
      })
    })

    return items
  }

  @computed
  get itemsNormalized(): ICartItem[] {
    return Array.from(this.items.values())
  }

  @computed
  get minimumDollarAmountHasBeenMet(): boolean {
    return this.dollarAmount >= CONFIG.cart.minimumDollarAmount
  }

  @computed
  get productAmount(): number {
    return this.itemsNormalized.reduce((prev, { quantity }) => prev + quantity, 0)
  }
}

export const cartStore = new CartStore()
